import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import PropTypes from "prop-types";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import {
  Bell,
  CalendarDays,
  Clock,
  ArrowLeft,
  ArrowRight,
  Search,
  FileText,
  Download,
  ExternalLink,
  Image as ImageIcon,
  Newspaper,
  GraduationCap,
  Share2,
  ChevronRight,
  AlertCircle,
  RefreshCw,
  X,
} from "lucide-react";

import LogoFallback from "/assets/img/bubbanlogo.jpg";

const API_BASE = import.meta.env.VITE_API_BASE_URL;
const API_SOURCE = import.meta.env.VITE_API_SOURCE_URL;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 22 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: "easeOut" },
  },
};

function formatDate(dateStr) {
  if (!dateStr) return "Date unavailable";

  // Handle ISO and YYYY-MM-DD date strings without shifting
  // calendar dates due to timezone conversion.
  const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);

  let date;

  if (match) {
    date = new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3])
    );
  } else {
    date = new Date(dateStr);
  }

  if (Number.isNaN(date.getTime())) return dateStr;

  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function getFileMimeType(url) {
  if (!url) return "unknown";

  const cleanUrl = String(url).split("?")[0].split("#")[0];
  const extension = cleanUrl.split(".").pop()?.toLowerCase();

  if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(extension)) {
    return "image";
  }

  if (extension === "pdf") return "pdf";

  return "unknown";
}

function getNoticeImageUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;

  return `${API_SOURCE || ""}/${String(path).replace(/^\/+/, "")}`;
}

function getNoticePayload(data) {
  if (Array.isArray(data)) return data[0] || null;

  if (data && typeof data === "object") {
    if (Array.isArray(data.data)) return data.data[0] || null;
    return data;
  }

  return null;
}

export default function NoticeDetails({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "Notice | Bandarban University",
}) {
  const { id } = useParams();

  const [allnotice, setAllnotice] = useState([]);
  const [selectedNotice, setSelectedNotice] = useState(null);

  const [loading, setLoading] = useState(true);
  const [recentLoading, setRecentLoading] = useState(true);
  const [error, setError] = useState("");
  const [recentError, setRecentError] = useState("");

  const [search, setSearch] = useState("");
  const [copied, setCopied] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Fetch the selected notice whenever the URL ID changes.
  useEffect(() => {
    let cancelled = false;

    async function fetchNotice() {
      setLoading(true);
      setError("");
      setSelectedNotice(null);
      setPreviewOpen(false);

      try {
        const response = await axios.post(
          `${API_BASE}/notice-details`,
          {
            userId: import.meta.env.VITE_API_USER,
            password: import.meta.env.VITE_API_PASS,
            id,
          },
          {
            headers: { "Content-Type": "application/json" },
          }
        );

        if (cancelled) return;

        const result = getNoticePayload(response.data);

        if (!result) {
          setError("The requested notice could not be found.");
          return;
        }

        setSelectedNotice(result);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.message ||
              err.message ||
              "Unable to load this notice. Please try again."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (id) {
      fetchNotice();
    } else {
      setLoading(false);
      setError("Notice ID is missing.");
    }

    return () => {
      cancelled = true;
    };
  }, [id]);

  // Fetch recent notices independently.
  useEffect(() => {
    let cancelled = false;

    async function fetchRecentNotices() {
      setRecentLoading(true);
      setRecentError("");

      try {
        const response = await axios.post(
          `${API_BASE}/recent-notice`,
          {
            userId: import.meta.env.VITE_API_USER,
            password: import.meta.env.VITE_API_PASS,
            department_id: "0",
          },
          {
            headers: { "Content-Type": "application/json" },
          }
        );

        if (cancelled) return;

        const data = response.data;
        const records = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];

        setAllnotice(records);
      } catch (err) {
        if (!cancelled) {
          setRecentError(
            err.response?.data?.message ||
              "Recent notices are temporarily unavailable."
          );
        }
      } finally {
        if (!cancelled) setRecentLoading(false);
      }
    }

    fetchRecentNotices();

    return () => {
      cancelled = true;
    };
  }, []);

  // Update browser tab title.
  useEffect(() => {
    document.title = selectedNotice?.head_line
      ? `${selectedNotice.head_line} | Bandarban University`
      : pageTitle;
  }, [pageTitle, selectedNotice]);

  // Filter recent notices.
  const filteredNotices = useMemo(() => {
    const query = search.trim().toLowerCase();

    return allnotice
      .filter((notice) => String(notice.id) !== String(id))
      .filter((notice) => {
        if (!query) return true;

        return String(notice.head_line || "")
          .toLowerCase()
          .includes(query);
      })
      .slice(0, 15);
  }, [allnotice, search, id]);

  const fileUrl = getNoticeImageUrl(selectedNotice?.images_path);
  const fileType = getFileMimeType(selectedNotice?.images_path);

  async function handleShare() {
    const url = window.location.href;

    try {
      if (navigator.share) {
        await navigator.share({
          title: selectedNotice?.head_line || "University Notice",
          url,
        });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2500);
      } else {
        window.prompt("Copy this notice URL:", url);
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        setError("Unable to share the notice.");
      }
    }
  }

  const dateParts = String(selectedNotice?.date || "").split("-");
  const noticeDay = dateParts[2]?.slice(0, 2) || "--";

  return (
    <main
      className="min-h-screen overflow-x-clip bg-slate-50 text-slate-800"
      style={{ "--notice-accent": accent }}
    >
      {/* =====================================================
          HERO SECTION
      ===================================================== */}
      <section className="relative isolate overflow-hidden bg-gradient-to-br from-green-950 via-green-800 to-emerald-700 text-white">
        {/* Animated background */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <motion.div
            animate={{
              x: [0, 30, 0],
              y: [0, -20, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -right-24 -top-28 h-96 w-96 rounded-full bg-emerald-300/15 blur-3xl"
          />

          <motion.div
            animate={{
              x: [0, -20, 0],
              y: [0, 20, 0],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -bottom-40 left-1/4 h-96 w-96 rounded-full bg-teal-300/15 blur-3xl"
          />

          <div
            className="absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage:
                "radial-gradient(circle, white 1px, transparent 1px)",
              backgroundSize: "25px 25px",
            }}
          />
        </div>

        <div className="relative mx-auto max-w-7xl px-5 pb-20 pt-8 sm:px-8 sm:pb-24 sm:pt-10 lg:px-10 lg:pb-28">
          {/* Breadcrumb */}
          <motion.nav
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12 flex flex-wrap items-center gap-2 text-sm text-green-50/75"
            aria-label="Breadcrumb"
          >
            <Link to="/" className="transition hover:text-white">
              Home
            </Link>

            <ChevronRight size={15} />

            <Link
              to="/all-notice"
              className="transition hover:text-white"
            >
              Notices
            </Link>

            <ChevronRight size={15} />

            <span className="max-w-[200px] truncate text-emerald-200">
              Notice Details
            </span>
          </motion.nav>

          <div className="grid items-center gap-10 lg:grid-cols-[1fr_auto]">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.65 }}
              className="min-w-0"
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] backdrop-blur-md sm:text-sm">
                <Bell size={16} className="text-emerald-300" />
                Official University Notice
              </span>

              <AnimatePresence mode="wait">
                {loading ? (
                  <motion.div
                    key="hero-loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="mt-7 space-y-4"
                  >
                    <div className="h-10 w-4/5 animate-pulse rounded-xl bg-white/15" />
                    <div className="h-10 w-3/5 animate-pulse rounded-xl bg-white/10" />
                    <div className="h-4 w-48 animate-pulse rounded bg-white/10" />
                  </motion.div>
                ) : (
                  <motion.div
                    key={selectedNotice?.id || "hero-error"}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h1 className="mt-7 max-w-4xl break-words text-3xl font-black leading-tight tracking-tight sm:text-4xl md:text-5xl lg:text-6xl">
                      {selectedNotice?.head_line || "Notice Details"}
                    </h1>

                    <p className="mt-5 max-w-2xl text-sm leading-7 text-green-50/85 sm:text-lg">
                      Stay informed about the latest announcements,
                      important updates, and official communications from
                      Bandarban University.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-medium backdrop-blur-md">
                  <CalendarDays
                    size={17}
                    className="text-emerald-300"
                  />
                  {selectedNotice
                    ? formatDate(selectedNotice.date)
                    : "Loading date..."}
                </span>

                <button
                  type="button"
                  onClick={handleShare}
                  disabled={!selectedNotice}
                  className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-sm font-semibold transition hover:bg-white/20 disabled:opacity-50"
                >
                  {copied ? (
                    <span>Link Copied!</span>
                  ) : (
                    <>
                      <Share2 size={16} />
                      Share Notice
                    </>
                  )}
                </button>
              </div>
            </motion.div>

            {/* Floating university logo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, rotate: -8 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="hidden justify-center lg:flex"
            >
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="relative flex h-48 w-48 items-center justify-center rounded-[2rem] border border-white/20 bg-white/10 p-7 shadow-2xl backdrop-blur-xl xl:h-56 xl:w-56"
              >
                <div className="absolute inset-3 rounded-[1.5rem] border border-white/10" />

                <img
                  src={logoSrc}
                  alt="Bandarban University Logo"
                  className="relative h-full w-full object-contain drop-shadow-lg"
                />
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 w-full">
          <svg
            viewBox="0 0 1440 80"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
            className="block h-10 w-full sm:h-16"
          >
            <path
              d="M0,40 C240,90 480,0 720,35 C960,70 1200,15 1440,35 L1440 80 L0 80 Z"
              fill="#f8fafc"
            />
          </svg>
        </div>
      </section>

      {/* =====================================================
          MAIN CONTENT
      ===================================================== */}
      <section className="relative mx-auto max-w-7xl px-4 py-10 sm:px-8 sm:py-14 lg:px-10 lg:py-16">
        <div className="grid min-w-0 grid-cols-1 items-start gap-8 lg:grid-cols-3">
          {/* LEFT: NOTICE CONTENT */}
          <motion.article
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="min-w-0 overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_12px_50px_rgba(15,23,42,0.055)] lg:col-span-2"
          >
            {/* Article heading */}
            <div className="border-b border-slate-100 p-5 sm:p-8">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="inline-flex items-center gap-2 rounded-full bg-green-50 px-3 py-2 text-xs font-bold uppercase tracking-wider text-green-800">
                  <Newspaper size={15} />
                  Notice Announcement
                </span>

                {selectedNotice && (
                  <span className="flex items-center gap-2 text-xs text-slate-500">
                    <Clock size={14} />
                    Published {formatDate(selectedNotice.date)}
                  </span>
                )}
              </div>

              <AnimatePresence mode="wait">
                {loading ? (
                  <motion.div
                    key="article-loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="mt-6 space-y-4"
                  >
                    <div className="h-7 w-4/5 animate-pulse rounded-lg bg-slate-100" />
                    <div className="h-4 w-2/5 animate-pulse rounded-lg bg-slate-100" />
                    <div className="h-4 w-full animate-pulse rounded-lg bg-slate-100" />
                    <div className="h-4 w-11/12 animate-pulse rounded-lg bg-slate-100" />
                  </motion.div>
                ) : error ? (
                  <motion.div
                    key="article-error"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mt-6 rounded-2xl border border-red-100 bg-red-50 p-6"
                  >
                    <AlertCircle
                      size={30}
                      className="text-red-600"
                    />
                    <h2 className="mt-3 font-bold text-red-900">
                      Unable to load notice
                    </h2>
                    <p className="mt-2 break-words text-sm leading-6 text-red-700">
                      {error}
                    </p>
                    <button
                      type="button"
                      onClick={() => window.location.reload()}
                      className="mt-4 inline-flex items-center gap-2 rounded-lg bg-red-700 px-4 py-2.5 text-sm font-bold text-white hover:bg-red-800"
                    >
                      <RefreshCw size={15} />
                      Try Again
                    </button>
                  </motion.div>
                ) : selectedNotice ? (
                  <motion.div
                    key={`article-${selectedNotice.id || id}`}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h2 className="mt-6 break-words text-2xl font-black leading-snug text-slate-900 sm:text-3xl">
                      {selectedNotice.head_line}
                    </h2>

                    <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-slate-500">
                      <span className="flex items-center gap-2">
                        <CalendarDays
                          size={16}
                          className="text-green-700"
                        />
                        {formatDate(selectedNotice.date)}
                      </span>

                      <span className="h-1 w-1 rounded-full bg-slate-300" />

                      <span className="flex items-center gap-2">
                        <GraduationCap
                          size={17}
                          className="text-green-700"
                        />
                        Bandarban University
                      </span>
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>

            {/* Article body */}
            {!loading && !error && selectedNotice && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.45, delay: 0.1 }}
                className="p-5 sm:p-8"
              >
                {/* Notice description */}
                {selectedNotice.description && (
                  <div className="mb-8 rounded-2xl border border-green-100 bg-gradient-to-br from-green-50/80 to-white p-5 sm:p-6">
                    <div className="mb-3 flex items-center gap-2 text-sm font-bold text-green-900">
                      <span className="h-6 w-1 rounded-full bg-green-600" />
                      Notice Information
                    </div>

                    <div className="whitespace-pre-line break-words text-sm leading-8 text-slate-700 sm:text-base">
                      {selectedNotice.description}
                    </div>
                  </div>
                )}

                {/* Attachment section */}
                {fileType === "image" && (
                  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-4 sm:px-5">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-50 text-green-800">
                          <ImageIcon size={20} />
                        </div>
                        <div>
                          <h3 className="font-bold text-slate-900">
                            Notice Attachment
                          </h3>
                          <p className="text-xs text-slate-500">
                            Image preview
                          </p>
                        </div>
                      </div>

                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700 transition hover:border-green-300 hover:bg-green-50 hover:text-green-800"
                      >
                        <ExternalLink size={14} />
                        Open Image
                      </a>
                    </div>

                    <button
                      type="button"
                      onClick={() => setPreviewOpen(true)}
                      className="block w-full cursor-zoom-in p-3 sm:p-5"
                      aria-label="Enlarge notice attachment"
                    >
                      <img
                        src={fileUrl}
                        alt={`Attachment for ${selectedNotice.head_line}`}
                        className="mx-auto max-h-[750px] w-full rounded-xl object-contain transition duration-300 hover:brightness-[0.98]"
                        loading="lazy"
                      />
                    </button>
                  </div>
                )}

                {fileType === "pdf" && (
                  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-4 sm:px-5">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-600">
                          <FileText size={20} />
                        </div>
                        <div>
                          <h3 className="font-bold text-slate-900">
                            Official Notice Document
                          </h3>
                          <p className="text-xs text-slate-500">
                            PDF document
                          </p>
                        </div>
                      </div>

                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-lg bg-green-800 px-3 py-2 text-xs font-bold text-white transition hover:bg-green-900"
                      >
                        <ExternalLink size={14} />
                        Open PDF
                      </a>
                    </div>

                    <div className="p-2 sm:p-4">
                      <iframe
                        src={fileUrl}
                        title={`PDF notice: ${selectedNotice.head_line}`}
                        className="h-[500px] w-full rounded-xl border border-slate-200 bg-white sm:h-[750px]"
                      />
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-white px-4 py-4">
                      <p className="text-xs text-slate-500">
                        If the PDF does not display, open it in a new tab.
                      </p>

                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-bold text-green-800 hover:underline"
                      >
                        <Download size={16} />
                        View Document
                      </a>
                    </div>
                  </div>
                )}

                {selectedNotice.images_path && fileType === "unknown" && (
                  <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <AlertCircle
                      size={20}
                      className="mt-0.5 shrink-0 text-amber-700"
                    />
                    <div>
                      <p className="font-bold text-amber-900">
                        Attachment preview unavailable
                      </p>
                      <p className="mt-1 text-sm leading-6 text-amber-800">
                        This attachment format cannot be previewed here.
                      </p>
                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 inline-flex items-center gap-2 text-sm font-bold text-amber-900 underline"
                      >
                        Open attachment
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  </div>
                )}

                {!selectedNotice.description &&
                  !selectedNotice.images_path && (
                    <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center">
                      <FileText
                        size={32}
                        className="mx-auto text-slate-300"
                      />
                      <p className="mt-3 text-sm text-slate-500">
                        No additional description or attachment is available.
                      </p>
                    </div>
                  )}

                {/* Bottom actions */}
                <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-slate-100 pt-6">
                  <Link
                    to="/all-notice"
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:border-green-200 hover:bg-green-50 hover:text-green-800"
                  >
                    <ArrowLeft size={17} />
                    All Notices
                  </Link>

                  <button
                    type="button"
                    onClick={handleShare}
                    className="inline-flex items-center gap-2 rounded-xl bg-green-800 px-4 py-3 text-sm font-bold text-white transition hover:bg-green-900"
                  >
                    <Share2 size={16} />
                    Share This Notice
                  </button>
                </div>
              </motion.div>
            )}
          </motion.article>

          {/* RIGHT: RECENT NOTICES */}
          <motion.aside
            initial={{ opacity: 0, x: 25 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="min-w-0 overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_12px_50px_rgba(15,23,42,0.055)]"
          >
            {/* Sidebar heading */}
            <div className="relative overflow-hidden bg-gradient-to-br from-green-900 via-green-800 to-emerald-600 p-5 text-white sm:p-6">
              <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-white/10 blur-xl" />

              <div className="relative flex items-center justify-between gap-3">
                <div>
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-green-100">
                    Stay Updated
                  </span>
                  <h2 className="mt-2 text-xl font-black">
                    Recent Notices
                  </h2>
                  <p className="mt-1 text-xs text-green-50/75">
                    Latest university announcements
                  </p>
                </div>

                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/20 bg-white/10">
                  <Bell size={22} />
                </div>
              </div>

              <div className="relative mt-5 flex items-center gap-2 rounded-xl border border-white/15 bg-white/10 px-3 py-2.5">
                <Newspaper size={17} className="shrink-0 text-green-100" />
                <span className="text-sm font-semibold">
                  {allnotice.length} notices available
                </span>
              </div>
            </div>

            {/* Search */}
            <div className="border-b border-slate-100 p-4">
              <div className="relative">
                <Search
                  size={17}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />

                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search notices..."
                  aria-label="Search recent notices"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3 pl-10 pr-9 text-sm outline-none transition focus:border-green-600 focus:bg-white focus:ring-4 focus:ring-green-100"
                />

                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    aria-label="Clear search"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  >
                    <X size={15} />
                  </button>
                )}
              </div>
            </div>

            {/* Recent notice list */}
            <div className="p-3 sm:p-4">
              {recentLoading ? (
                <div className="space-y-3 p-2">
                  {[1, 2, 3, 4, 5].map((item) => (
                    <div
                      key={item}
                      className="flex gap-3 rounded-xl p-3"
                    >
                      <div className="h-12 w-12 shrink-0 animate-pulse rounded-xl bg-slate-100" />
                      <div className="min-w-0 flex-1 space-y-2 py-1">
                        <div className="h-3 w-1/3 animate-pulse rounded bg-slate-100" />
                        <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                        <div className="h-3 w-3/4 animate-pulse rounded bg-slate-100" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : recentError ? (
                <div className="p-6 text-center">
                  <AlertCircle
                    size={28}
                    className="mx-auto text-amber-600"
                  />
                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    {recentError}
                  </p>
                </div>
              ) : filteredNotices.length > 0 ? (
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  className="space-y-1"
                >
                  {filteredNotices.map((notice, index) => {
                    const parts = String(notice.date || "").split("-");
                    const day = parts[2]?.slice(0, 2) || "--";

                    return (
                      <motion.div
                        variants={itemVariants}
                        key={notice.id}
                        whileHover={{ x: 3 }}
                      >
                        <Link
                          to={`/notice/${notice.id}`}
                          className="group flex min-w-0 gap-3 rounded-xl border border-transparent p-3 transition-all duration-200 hover:border-green-100 hover:bg-green-50/80"
                        >
                          {/* Date */}
                          <div className="flex h-[52px] w-[49px] shrink-0 flex-col items-center justify-center rounded-xl border border-green-100 bg-green-50 text-green-800 transition-colors group-hover:border-green-700 group-hover:bg-green-700 group-hover:text-white">
                            <span className="text-lg font-black leading-none">
                              {day}
                            </span>
                            <span className="mt-1 text-[9px] font-bold uppercase">
                              {parts[1]
                                ? new Date(
                                    Number(parts[0]),
                                    Number(parts[1]) - 1,
                                    1
                                  ).toLocaleDateString("en-GB", {
                                    month: "short",
                                  })
                                : "Notice"}
                            </span>
                          </div>

                          {/* Title */}
                          <div className="min-w-0 flex-1">
                            <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                              {formatDate(notice.date)}
                            </p>

                            <h3 className="line-clamp-2 break-words text-sm font-semibold leading-5 text-slate-700 transition-colors group-hover:text-green-800">
                              {notice.head_line}
                            </h3>
                          </div>

                          <ChevronRight
                            size={17}
                            className="mt-4 shrink-0 text-slate-300 transition-all group-hover:translate-x-1 group-hover:text-green-700"
                          />
                        </Link>
                      </motion.div>
                    );
                  })}
                </motion.div>
              ) : (
                <div className="p-8 text-center">
                  <Search
                    size={30}
                    className="mx-auto text-slate-300"
                  />
                  <p className="mt-3 text-sm font-semibold text-slate-700">
                    No notices found
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Try a different search keyword.
                  </p>
                </div>
              )}

              {/* View all notices */}
              <div className="mt-4 border-t border-slate-100 pt-4">
                <Link
                  to="/all-notice"
                  className="group flex items-center justify-center gap-2 rounded-xl bg-green-50 px-4 py-3 text-sm font-bold text-green-800 transition hover:bg-green-800 hover:text-white"
                >
                  View All Notices
                  <ArrowRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </Link>
              </div>
            </div>
          </motion.aside>
        </div>

        {/* Bottom university banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="relative mt-10 overflow-hidden rounded-3xl border border-green-100 bg-gradient-to-r from-green-50 via-white to-emerald-50 p-5 sm:p-7"
        >
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-green-800 text-white shadow-lg">
                <GraduationCap size={24} />
              </div>

              <div>
                <h2 className="font-extrabold text-slate-900">
                  Bandarban University
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Stay informed. Stay connected.
                </p>
              </div>
            </div>

            <Link
              to="/all-notice"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-green-200 bg-white px-5 py-3 text-sm font-bold text-green-800 transition hover:bg-green-800 hover:text-white"
            >
              Browse All Announcements
              <ArrowRight size={17} />
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Image lightbox */}
      <AnimatePresence>
        {previewOpen && fileType === "image" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/90 p-4 backdrop-blur-sm"
            onClick={() => setPreviewOpen(false)}
          >
            <button
              type="button"
              aria-label="Close image preview"
              onClick={() => setPreviewOpen(false)}
              className="absolute right-5 top-5 rounded-xl bg-white/10 p-3 text-white transition hover:bg-white/20"
            >
              <X size={24} />
            </button>

            <motion.img
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.25 }}
              src={fileUrl}
              alt={selectedNotice?.head_line || "Notice attachment"}
              className="max-h-[90vh] max-w-full rounded-xl object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}

NoticeDetails.propTypes = {
  logoSrc: PropTypes.string,
  accent: PropTypes.string,
  pageTitle: PropTypes.string,
};